import * as React from 'react';
import { useRef, useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { Search } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type?: string;
  hotkey?: {
    keyboard?: string;
    controller?: string;
  };
  isSearch?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', hotkey, onChange, value, isSearch, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [internalValue, setInternalValue] = useState('');

    // Handle controlled vs uncontrolled input
    const isControlled = value !== undefined;
    const currentValue = isControlled ? (value as string) : internalValue;

    const { handleInput, lastInterimRef } = useSpeechRecognition({
      inputRef,
      setInternalValue,
      isRecording,
      setIsRecording,
      isControlled,
      currentValue,
      value,
      onChange,
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (hotkey?.keyboard && event.key === hotkey.keyboard) {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <>
        {isRecording && (
          <div className="absolute -top-7 left-0 inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-red-500 ring-1 ring-inset ring-red-500/20">
            <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
            Recording in progress
          </div>
        )}
        <div className="relative w-full">
          {isSearch && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
              <Search className="h-4 w-4 text-lime/60" />
            </div>
          )}
          <input
            type={type}
            className={cn(
              'flex h-10 w-full rounded-md border border-[#2E3835] bg-[#111] px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-white/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:ring-2 focus-visible:border-transparent focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              isSearch && 'pl-8',
              className
            )}
            value={
              (currentValue || '') +
              (lastInterimRef.current ? ' ' + lastInterimRef.current + '🎤' : '')
            }
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            ref={(element) => {
              if (typeof ref === 'function') {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
              inputRef.current = element;
            }}
            {...props}
          />
        </div>
      </>
    );
  }
);

Input.displayName = 'Input';

export { Input };
